import React, { useState, useEffect, useRef } from 'react';
import {
  IoArrowForward,
  IoBuildOutline,
  IoChatboxEllipsesOutline,
  IoDocumentTextOutline,
  IoLibraryOutline,
  IoLocationOutline,
  IoTrendingUpOutline
} from 'react-icons/io5';
import './AtlasNewChatMenu.css';

const MENU_COLUMNS = [
  {
    id: 'find',
    title: 'Find It',
    subtitle: 'Evaluate markets, land, and opportunities',
    icon: IoLocationOutline,
    prompts: [
      'A piece of land just hit my desk - quick read',
      "I'm exploring a new market for expansion",
      "What's changed in my market this month?",
      "Where's the demand that nobody's building for yet?"
    ]
  },
  {
    id: 'plan',
    title: 'Plan It',
    subtitle: 'Dial in your product, pricing, and financials',
    icon: IoBuildOutline,
    prompts: [
      'How should I price my next spec?',
      'What features are actually moving the needle in my market?',
      'Stress-test my release schedule',
      "I'm working on a pro forma - what am I missing?"
    ]
  },
  {
    id: 'sell',
    title: 'Sell It',
    subtitle: 'Arm your team with playbooks, scripts, and campaigns',
    icon: IoTrendingUpOutline,
    prompts: [
      'How do I stack up against the nationals in my market?',
      'Build me a sales playbook for my newest community',
      'Turn my spec sheet into training my team can use tomorrow',
      'Help me write copy that sells what makes us different'
    ]
  }
];

// Random starting indices, computed once per mount
function getRandomStart(length) {
  return Math.floor(Math.random() * length);
}

function RotatingPrompt({ prompts, onSelect, advanceSignal }) {
  const [startIndex] = useState(() => getRandomStart(prompts.length));
  const [offset, setOffset] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const timeoutRef = useRef(null);
  const prevSignal = useRef(advanceSignal);

  const index = (startIndex + offset) % prompts.length;

  useEffect(() => {
    if (advanceSignal !== prevSignal.current) {
      prevSignal.current = advanceSignal;
      setIsFading(true);
      timeoutRef.current = setTimeout(() => {
        setOffset((prev) => prev + 1);
        setIsFading(false);
      }, 300);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [advanceSignal]);

  return (
    <button
      type="button"
      className="atlas-menu-ticker-btn"
      onClick={() => onSelect(prompts[index])}
    >
      <span className={`atlas-menu-ticker-text ${isFading ? 'atlas-menu-ticker-text-fading' : ''}`}>
        {prompts[index]}
      </span>
      <IoArrowForward className="atlas-menu-ticker-arrow" />
    </button>
  );
}

const CYCLE_INTERVAL = 10000; // 10 seconds between each column rotation
const NUM_COLUMNS = MENU_COLUMNS.length;

function getCompanyPrompts(companyName) {
  const label = (companyName || '').trim() || '[Company Name]';
  return [
    {
      id: 'work',
      Icon: IoLibraryOutline,
      text: 'Get me caught up on what the company has been working on.'
    },
    {
      id: 'copy',
      Icon: IoDocumentTextOutline,
      text: `Help me write copy that sells what makes ${label} different, using our shared brand guide and recent work.`
    },
    {
      id: 'answer',
      Icon: IoChatboxEllipsesOutline,
      text: 'Use our shared company workspace to help me answer: [question].'
    }
  ];
}

export default function AtlasNewChatMenu({ onSelectPrompt, isCompanyMode = false, companyName = '' }) {
  // Signals: [0, 0, 0] — increment the column's signal to tell it to advance
  const [signals, setSignals] = useState([0, 0, 0]);
  const currentCol = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (pausedRef.current) return;
      const col = currentCol.current;
      setSignals((prev) => {
        const next = [...prev];
        next[col] = prev[col] + 1;
        return next;
      });
      currentCol.current = (col + 1) % NUM_COLUMNS;
    }, CYCLE_INTERVAL);

    return () => clearInterval(timer);
  }, []);

  if (isCompanyMode) {
    const prompts = getCompanyPrompts(companyName);
    return (
      <div className="atlas-menu atlas-menu-company">
        <div className="atlas-menu-company-prompts" aria-label="Company mode starter prompts">
          {prompts.map(({ id, Icon, text }) => (
            <button
              key={id}
              type="button"
              className={`atlas-menu-company-prompt atlas-menu-company-prompt-${id}`}
              onClick={() => onSelectPrompt(text)}
            >
              <span className="atlas-menu-company-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="atlas-menu-company-text">{text}</span>
              <IoArrowForward className="atlas-menu-ticker-arrow" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="atlas-menu">
      <div
        className="atlas-menu-ticker"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        {MENU_COLUMNS.map((col, i) => {
          const IconComponent = col.icon;
          return (
            <div key={col.id} className="atlas-menu-ticker-col">
              <div className="atlas-menu-ticker-header">
                <div className="atlas-menu-ticker-badge">
                  <IconComponent className="atlas-menu-ticker-badge-icon" />
                </div>
                <span className="atlas-menu-ticker-label">{col.title}</span>
              </div>
              <RotatingPrompt
                prompts={col.prompts}
                onSelect={onSelectPrompt}
                advanceSignal={signals[i]}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
